import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { setSupportCursor, removeSupportCursor, setActiveSession } from '../../middleware/redux/reducers/support.reducer';
import { io } from 'socket.io-client';
import { graphqlWsEndpoint } from '../../config/socket';

const SOCKET_PATH = '/ws/socket.io/';

let supportSocket = null;

function getSocket(token) {
  if (supportSocket?.connected) return supportSocket;
  if (supportSocket) {
    supportSocket.disconnect();
    supportSocket = null;
  }
  supportSocket = io(`${graphqlWsEndpoint}/support`, {
    path: SOCKET_PATH,
    auth: { token },
    transports: ['websocket'],
    reconnectionAttempts: 5,
  });
  return supportSocket;
}

// Build a stable CSS selector for an element so it can be found on the tenant's DOM.
// Priority: id → stable data attributes → href (for links) → nth-of-type path
function buildSelector(el) {
  const parts = [];
  let current = el;

  for (let depth = 0; depth < 7; depth++) {
    if (!current || current === document.body) break;

    // ID is globally unique — stop traversal
    if (current.id && !/^\d/.test(current.id) && !/\s/.test(current.id)) {
      parts.unshift(`#${current.id}`);
      break;
    }

    let part = current.tagName.toLowerCase();

    // Stable Ant Design / custom data attributes
    for (const attr of ['data-testid', 'data-node-key', 'data-row-key', 'data-menu-id', 'data-key']) {
      const val = current.getAttribute(attr);
      if (val) {
        part += `[${attr}="${val}"]`;
        parts.unshift(part);
        return parts.join(' > ');
      }
    }

    // href for anchor tags — most reliable for navigation links
    if (current.tagName === 'A') {
      const href = current.getAttribute('href');
      if (href) {
        parts.unshift(`a[href="${href}"]`);
        return parts.join(' > ');
      }
    }

    // Disambiguate using nth-of-type among siblings
    const parent = current.parentElement;
    if (parent) {
      const same = Array.from(parent.children).filter(c => c.tagName === current.tagName);
      if (same.length > 1) {
        part += `:nth-of-type(${same.indexOf(current) + 1})`;
      }
    }

    parts.unshift(part);
    current = current.parentElement;
  }

  return parts.join(' > ');
}

// Replay a click on the tenant side.
// Tries: href navigation → CSS selector → coordinate fallback
function replayClick({ selector, href, x, y }, navigate) {
  // Internal navigation links — use React Router for SPA navigation
  if (href && (href.startsWith('/') || href.startsWith('#'))) {
    navigate(href);
    return;
  }

  // Try CSS selector
  try {
    const el = document.querySelector(selector);
    if (el) {
      el.click();
      return;
    }
  } catch (_) {}

  // Coordinate fallback — works when both viewports have the same width
  if (x != null && y != null) {
    const el = document.elementFromPoint(x * window.innerWidth, y * window.innerHeight);
    if (el && el !== document.body) el.click();
  }
}

function CursorArrow({ color }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20">
      <polygon
        points="0,0 0,14 4,10 8,18 10,17 6,9 11,9"
        fill={color}
        stroke="#fff"
        strokeWidth="1"
      />
    </svg>
  );
}

export function RemoteCursor({ sessionId }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const cursors = useSelector((state) => state.support?.cursors ?? {});
  const activeSession = useSelector((state) => state.support?.activeSession);
  const activeSessionRef = useRef(activeSession);
  const throttleRef = useRef(null);
  const socketRef = useRef(null);
  const navigateRef = useRef(navigate);

  // Detect which side this user is on
  const isSupportSide = !!sessionStorage.getItem('supportSessionToken');
  const isTenantSide = !!sessionStorage.getItem('supportTenantSocketToken');

  // Keep refs current to avoid stale closures
  useEffect(() => { activeSessionRef.current = activeSession; }, [activeSession]);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  // ── Main socket setup ───────────────────────────────────────────────
  useEffect(() => {
    if (!sessionId) return;

    const token =
      sessionStorage.getItem('supportSessionToken') ||
      sessionStorage.getItem('supportTenantSocketToken');
    if (!token) return;

    const socket = getSocket(token);
    socketRef.current = socket;

    function joinRoom() {
      socket.emit('support:join', { sessionId });
    }
    socket.on('connect', joinRoom);
    if (socket.connected) joinRoom();

    // ── Cursor events ──
    function onCursor(data) {
      dispatch(setSupportCursor({ id: data.id, cursor: { x: data.x, y: data.y, label: data.label } }));
    }
    function onLeave(data) {
      dispatch(removeSupportCursor(data.id));
    }
    function onConnected() {
      const current = activeSessionRef.current;
      if (!current) return;
      dispatch(setActiveSession({ ...current, status: 'active' }));
    }
    function onEnded() {
      dispatch({ type: 'support/clearSupport' });
      sessionStorage.removeItem('supportSessionToken');
      sessionStorage.removeItem('supportTenantSocketToken');
    }

    // ── Co-browse: tenant receives navigation + action events ──
    function onNavigate({ path }) {
      if (isTenantSide) navigateRef.current(path);
    }
    function onAction({ type, selector, href, x, y }) {
      if (!isTenantSide) return;
      if (type === 'click') {
        replayClick({ selector, href, x, y }, navigateRef.current);
      }
    }

    socket.on('support:cursor', onCursor);
    socket.on('support:cursor_leave', onLeave);
    socket.on('support:connected', onConnected);
    socket.on('support:ended', onEnded);
    socket.on('support:navigate', onNavigate);
    socket.on('support:action', onAction);

    // ── Cursor broadcast (both sides show their cursor to the other) ──
    function handleMouseMove(e) {
      if (!socket.connected) return;
      if (throttleRef.current) return;
      throttleRef.current = setTimeout(() => { throttleRef.current = null; }, 50);
      socket.emit('support:cursor', {
        sessionId,
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      });
    }
    function handleMouseLeave() {
      if (socket.connected) socket.emit('support:cursor_leave');
    }

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      socket.off('connect', joinRoom);
      socket.off('support:cursor', onCursor);
      socket.off('support:cursor_leave', onLeave);
      socket.off('support:connected', onConnected);
      socket.off('support:ended', onEnded);
      socket.off('support:navigate', onNavigate);
      socket.off('support:action', onAction);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [sessionId, dispatch, isTenantSide]);

  // ── Support side: broadcast navigation changes ──────────────────────
  useEffect(() => {
    if (!isSupportSide || !sessionId) return;
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit('support:navigate', {
      sessionId,
      path: location.pathname + location.search,
    });
  }, [location.pathname, location.search, isSupportSide, sessionId]);

  // ── Support side: broadcast click events ────────────────────────────
  useEffect(() => {
    if (!isSupportSide || !sessionId) return;

    function handleClick(e) {
      const socket = socketRef.current;
      if (!socket?.connected) return;

      const anchor = e.target.closest('a');
      socket.emit('support:action', {
        sessionId,
        type: 'click',
        selector: buildSelector(e.target),
        href: anchor?.getAttribute('href') ?? null,
        // Normalised viewport coordinates as fallback
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      });
    }

    // Capture phase: fires before React Router intercepts link clicks
    window.addEventListener('click', handleClick, true);
    return () => window.removeEventListener('click', handleClick, true);
  }, [isSupportSide, sessionId]);

  // ── Render remote cursors ────────────────────────────────────────────
  return (
    <>
      {Object.entries(cursors).map(([id, cursor]) => (
        <div
          key={id}
          style={{
            position: 'fixed',
            left: `${cursor.x * 100}vw`,
            top: `${cursor.y * 100}vh`,
            pointerEvents: 'none',
            zIndex: 9999,
            transition: 'left 80ms linear, top 80ms linear',
          }}
        >
          <CursorArrow color="#1677ff" />
          <div
            style={{
              position: 'absolute',
              top: 18,
              left: 12,
              padding: '2px 8px',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600,
              color: '#fff',
              background: '#1677ff',
              whiteSpace: 'nowrap',
            }}
          >
            {cursor.label}
          </div>
        </div>
      ))}
    </>
  );
}
